import React, { useState } from 'react';
import { Mail, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { openCookieSettings } from './CookieBanner';
import { SOLUTIONS_MENU } from '../data/solutionsMenu';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [openGroups, setOpenGroups] = useState({});

  const footerLinks = {
    company: [
      { name: 'About', path: '/about' },
      { name: 'Contact', path: '/#contact' },
      { name: 'Distribution', path: '/distribution' },
      { name: 'Investors', path: '/investors' },
      { name: 'News', path: '/news' },
      { name: 'Press Kit', path: '/press' }
    ],
    support: [
      { name: 'Calibration & RMA', path: '/support/calibration-rma' },
      { name: 'Documentation', path: '/support/documentation' },
      { name: 'Warranty', path: '/support/warranty' },
    ],
    useCases: [
      { name: 'Engine Diagnostics', path: '/use-cases/engine-diagnostics' },
      { name: 'Valve Inspections', path: '/use-cases/valve-inspections' },
      { name: 'Landing Gear Inspections', path: '/use-cases/landing-gear-inspections' },
      { name: 'Hydraulic Systems', path: '/use-cases/hydraulic-systems' },
    ]
  };

  return (
    <footer className="bg-[#1e2d3b] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link
              to="/"
              className="mb-6 inline-block hover:opacity-80 transition-opacity duration-200"
              data-testid="footer-logo-link"
            >
              <img 
                src="/images/logo-for-website.png"
                alt="CTRL Systems"
                className="h-14 w-auto"
              />
            </Link>
            <p className="text-slate-400 leading-relaxed mb-6 max-w-sm">
              Ultrasonic sensors and acoustic cameras for advanced aviation maintenance. 
              Revolutionizing airframe and powerplant troubleshooting.
            </p>
            <div className="flex space-x-4">
              <a 
                href="mailto:jwatts@ctrlsys.com" 
                className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors duration-300"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-200 text-left"
                    data-testid={`footer-company-${link.name.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Links — collapsible groups mirroring the header dropdown */}
          <div>
            <h3 className="font-semibold text-white mb-4">GSTE Products</h3>
            <ul className="space-y-2">
              {SOLUTIONS_MENU.map((item) => {
                const slug = item.path.split('/').pop();
                const open = !!openGroups[slug];
                return (
                  <li key={item.path}>
                    <div className="flex items-center gap-1">
                      <Link
                        to={item.path}
                        className="flex-1 text-slate-400 hover:text-blue-400 transition-colors duration-200 text-left text-sm"
                        data-testid={`footer-solutions-${slug}`}
                      >
                        {item.name}
                      </Link>
                      {item.children && (
                        <button
                          type="button"
                          onClick={() => setOpenGroups((g) => ({ ...g, [slug]: !g[slug] }))}
                          aria-expanded={open}
                          aria-controls={`footer-solutions-${slug}-children`}
                          aria-label={`${open ? 'Collapse' : 'Expand'} ${item.name}`}
                          className="p-1 text-slate-500 hover:text-blue-400 transition-colors"
                          data-testid={`footer-solutions-${slug}-caret`}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {item.children && (
                      <ul id={`footer-solutions-${slug}-children`} className={`${open ? 'mt-1.5 space-y-1.5' : 'hidden'} pl-4 border-l border-slate-700`}>
                        {item.children.map((child) => (
                          <li key={child.path}>
                            <Link
                              to={child.path}
                              className="text-slate-500 hover:text-blue-400 transition-colors duration-200 text-sm"
                              data-testid={`footer-solutions-${slug}-child-${child.path.split('/').pop()}`}
                            >
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-200 text-left"
                    data-testid={`footer-support-${index}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Use Cases Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              <Link
                to="/use-cases"
                className="hover:text-blue-400 transition-colors duration-200"
                data-testid="footer-usecases-hub"
              >
                Use Cases
              </Link>
            </h3>
            <ul className="space-y-3">
              {footerLinks.useCases.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-200 text-left"
                    data-testid={`footer-usecase-${index}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-400 text-sm">
              © {currentYear} CTRL Systems, Inc. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link
                to="/legal/privacy"
                data-testid="footer-privacy-link"
                className="text-slate-400 hover:text-blue-400 transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link
                to="/legal/cookies"
                data-testid="footer-cookies-link"
                className="text-slate-400 hover:text-blue-400 transition-colors duration-200"
              >
                Cookies
              </Link>
              <button
                type="button"
                onClick={openCookieSettings}
                data-testid="footer-cookie-settings-link"
                className="text-slate-400 hover:text-blue-400 transition-colors duration-200"
              >
                Cookie Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
